/**
 * SIMS-9205 — "Enrollment limit reached", shared by Participants ("Enroll to new program") and
 * Beneficiaries ("Re-enroll").
 *
 * The server refuses a new enrollment record once the person holds the program's Enrollments per
 * person maximum with HTTP 422
 * `{ error: "ENROLLMENTS_PER_PERSON_REACHED", message, statusCode, count, maximum }`, the same
 * shape as the budget gate. The axios interceptor rejects with that body, so the code is at the
 * top level. The screens build their own text from the two numbers rather than showing the
 * server's message, so the person is the account's own word (SIMS-9208, passed in by the dialog).
 *
 * SIMS-9207: when the program counts within one organization unit, the refusal adds
 * `scope: "ORGANIZATION_UNIT"` and the unit's name (`organizationUnitName`), and the dialog uses the
 * per-unit wording. Any other `scope`, or none (an older backend), keeps the across-units wording.
 */

export const ENROLLMENTS_PER_PERSON_REACHED = "ENROLLMENTS_PER_PERSON_REACHED";

export const ENROLLMENT_LIMIT_TITLE = "Enrollment limit reached";

export const ORGANIZATION_UNIT_SCOPE = "ORGANIZATION_UNIT";

/** SIMS-9207 Q-18 — the wording when the server could not name the unit. */
export const UNNAMED_ORGANIZATION_UNIT = "this organization unit";

export interface EnrollmentLimitDetails {
	count: number;
	maximum: number;
	/** SIMS-9207: present only when the count was within one organization unit. */
	scope?: typeof ORGANIZATION_UNIT_SCOPE;
	organizationUnitName?: string;
}

const isWholeNumber = (value: unknown): value is number =>
	typeof value === "number" && Number.isInteger(value);

/** The two numbers from the refusal, or `null` when the error is anything else. */
export const getEnrollmentLimitDetails = (
	error: unknown,
): EnrollmentLimitDetails | null => {
	if (typeof error !== "object" || error === null) return null;
	const body = error as {
		error?: unknown;
		count?: unknown;
		maximum?: unknown;
		scope?: unknown;
		organizationUnitName?: unknown;
	};
	if (body.error !== ENROLLMENTS_PER_PERSON_REACHED) return null;
	// Without both numbers the agreed copy cannot be shown; the form's generic error handling
	// still reports the refusal.
	if (!isWholeNumber(body.count) || !isWholeNumber(body.maximum)) return null;
	if (body.scope !== ORGANIZATION_UNIT_SCOPE) {
		return { count: body.count, maximum: body.maximum };
	}
	const name =
		typeof body.organizationUnitName === "string"
			? body.organizationUnitName.trim()
			: "";
	return {
		count: body.count,
		maximum: body.maximum,
		scope: ORGANIZATION_UNIT_SCOPE,
		...(name && { organizationUnitName: name }),
	};
};

export const isEnrollmentLimitError = (error: unknown): boolean =>
	getEnrollmentLimitDetails(error) !== null;

/**
 * The agreed copy: across organization units (SIMS-9205), or within one unit naming it
 * (SIMS-9207). Count and maximum stay two separate numbers, and "1 enrollment" is singular
 * (Q-B / Q-6). It names a facility at most — no person and no health information (C-3).
 */
export const buildEnrollmentLimitMessage = ({
	count,
	maximum,
	scope,
	organizationUnitName,
	person = "beneficiary",
}: EnrollmentLimitDetails & { person?: string }): string => {
	const enrollments = count === 1 ? "enrollment" : "enrollments";
	const contact =
		"Please contact your account administrator if this limit needs to change.";
	if (scope === ORGANIZATION_UNIT_SCOPE) {
		const unit = organizationUnitName?.trim() || UNNAMED_ORGANIZATION_UNIT;
		return (
			`This ${person} has ${count} ${enrollments} in this program at ${unit}. ` +
			`The maximum allowed per organization unit is ${maximum}, so a new enrollment cannot be created. ` +
			contact
		);
	}
	return (
		`This ${person} has ${count} ${enrollments} in this program. ` +
		`The maximum allowed is ${maximum}, so a new enrollment cannot be created. ` +
		contact
	);
};
